// src/contexts/Web3Context.jsx

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { HybridBrowserProvider } from '../utils/HybridBrowserProvider';
import { createAppKit, useAppKit, useAppKitAccount, useAppKitProvider, useDisconnect } from '@reown/appkit/react';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { bsc, bscTestnet, mainnet } from '@reown/appkit/networks';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from '../firebaseConfig';
import XCoinSeriesA from '../abis/XCoinSeriesA.json';
import WalletGuideModal from '../components/WalletGuideModal';
import XCoinCrowdsale from '../abis/XCoinCrowdsale.json';
import XCoinBuyback from '../abis/XCoinBuyback.json';
import XCoinBNBExchange from '../abis/XCoinBNBExchange.json';
import MockUSDT from '../abis/MockUSDT.json';

// --- 1. AppKit Configuration ---

const projectId = '7e6ac3f1370b5397fa44a559a947840f'; // Project ID

// We MUST include other networks (like Mainnet) so the Provider initializes 
// even if the user starts on the wrong chain. Then we force the switch.
// Restore all networks to allow connection, then force switch in logic
// We include Mainnet again because removing it breaks the AppKit "Networks" modal (shows empty).
// We will handle the switch to BSC in the syncWeb3 logic.
const networks = [bsc, bscTestnet, mainnet];

const metadata = {
  name: 'XCOIN Dashboard',
  description: 'Conecta tu billetera para gestionar XCOIN',
  url: 'https://dashboard.xcoin.cl',
  icons: ['https://dashboard.xcoin.cl/logo.png']
};

// Configuración de WalletConnect con universalProviderConfigOverride
// Esto define explícitamente los métodos, chains y eventos para TrustWallet móvil
const ethersAdapter = new EthersAdapter();

createAppKit({
  adapters: [ethersAdapter],
  networks,
  defaultNetwork: bsc,
  metadata,
  projectId,
  features: {
    analytics: true,
    socials: [],
    email: false,
    emailShowWallets: true,
    connectMethodsOrder: ['wallet']
  },
  // Configuración explícita de namespaces para resolver error 5300 en móvil
  // RESTAURADO: Es necesario para que la conexión inicie.
  universalProviderConfigOverride: {
    methods: {
      eip155: [
        'eth_sendTransaction',
        'eth_signTransaction',
        'eth_sign',
        'personal_sign',
        'eth_signTypedData'
      ]
    },
    chains: {
      eip155: ['eip155:56', 'eip155:97', 'eip155:1'] // BSC Mainnet, BSC Testnet, Ethereum Mainnet
    },
    events: {
      eip155: ['chainChanged', 'accountsChanged']
    },
    rpcMap: {
      'eip155:56': 'https://bsc-dataseed.binance.org/',
      'eip155:97': 'https://data-seed-prebsc-1-s1.binance.org:8545/',
      'eip155:1': 'https://ethereum.publicnode.com'
    },
    defaultChain: 'eip155:56'
  }
});

// --- 2. Helper Functions ---

// Detect if user is on mobile device (iOS, Android, or tablet)
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  // Also check for touch points (typical for iPad in desktop mode)
  return mobileRegex.test(ua) || (navigator.maxTouchPoints > 0 && /Macintosh/.test(ua));
};

// Detect if user is on iOS/Safari
const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS || isSafari;
};

// Standalone WalletConnect config removed - AppKit handles WalletConnect internally

// Sleep utility for retry logic
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// --- 3. Context Creation ---

const Web3Context = createContext();

export const Web3Provider = ({ children }) => {
  // AppKit Hooks
  const { open } = useAppKit();
  const { address, isConnected, chainId } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider('eip155');
  const { disconnect } = useDisconnect(); // Native Disconnect Hook

  // INTERNAL LOGGING FOR DEBUGGING
  const [web3Logs, setWeb3Logs] = useState([]);
  const addLog = (msg) => {
    // Store as object for better handling in DiagnosticsView
    const newLog = {
      timestamp: new Date().toISOString(),
      message: msg
    };
    setWeb3Logs(prev => [newLog, ...prev].slice(0, 50));
    console.log(`[Web3Debug] ${msg}`);
  };

  // --- DISCONNECT LOGIC (CENTRALIZED) ---
  const deepCleanSession = async (reason = "Manual Disconnect") => {
    addLog(`Deep Clean triggered: ${reason}`);

    // 1. Force Clear Internal State IMMEDIATELY
    setWallet(null);
    setProvider(null);
    setSigner(null);
    setContract(null);

    // 2. Clear Local Storage (Aggressive cleanup to prevent auto-connect ghosts)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('wc@') ||
        key.startsWith('@w3m') ||
        key.startsWith('wagmi') ||
        key.includes('walletconnect')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    addLog(`Cleared ${keysToRemove.length} storage keys.`);

    // 3. Native AppKit Disconnect (RESTORED)
    if (disconnect) {
      try {
        await disconnect();
        addLog("AppKit disconnect successful.");
      } catch (e) {
        addLog(`AppKit disconnect warning: ${e.message}`);
      }
    }

    // 4. Reset Intent
    setBindingIntent(false);
  };


  // --- GLOBAL ERROR HANDLER ---
  useEffect(() => {
    const handleError = (message, source, lineno, colno, error) => {
      addLog(`Global Error: ${message} at ${source}:${lineno}`);
      return false;
    };

    const handleRejection = (event) => {
      addLog(`Unhandled Promise Rejection: ${event.reason}`);
    };

    window.onerror = handleError;
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.onerror = null;
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  // Wallet Guide Modal State
  const [showWalletGuide, setShowWalletGuide] = useState(false);

  // Internal State (to match legacy interface)
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [crowdsaleContract, setCrowdsaleContract] = useState(null);
  const [buybackContract, setBuybackContract] = useState(null);
  const [bnbAtmContract, setBnbAtmContract] = useState(null);
  const [bnbBuybackContract, setBnbBuybackContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [wallet, setWallet] = useState(null);

  // Constants (BSC Mainnet)
  const XCOIN_ADDRESS = "0x6508AEB49733f5dAAD56621Bc2AC05193F26b53a";
  // UPDATED: New Contract with Net Growth Logic (v3.3.16) - Force Refresh
  const CROWDSALE_ADDRESS = "0xE72203e517B80E3c85176376ad3f557538A8d118";
  const BUYBACK_ADDRESS = "0x3257ee32CbB390CB65a3638Aa56E77136F9cd178";
  const BNB_EXCHANGE_ADDRESS = "0x6DeF104b6542d323Ef6a2Bebad60309E85196d95";
  const USDT_ADDRESS = "0x55d398326f99059fF775485246999027B3197955"; // Binance-Peg BSC-USD

  // --- 3. Sync Logic ---

  // Force Re-Sync Trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Intent Flag: Distinguishes "User Clicked Connect" vs "Auto-Reconnect"
  // If true, we allow a connection even if not bound (so they can bind).
  // If false (page load/fresh), and not bound -> We disconnect.
  const [bindingIntent, setBindingIntent] = useState(false);

  // Listener for Mobile App Switching (Return to Tab) & Provider Events
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        addLog("App Foregrounded. Forcing Re-Sync...");
        setRefreshTrigger(prev => prev + 1);
      }
    };

    // Safari/iOS: visibilitychange is unreliable when returning from wallet apps
    const handlePageShow = (e) => {
      if (e.persisted) {
        addLog("Page restored from bfcache. Forcing Re-Sync...");
        setRefreshTrigger(prev => prev + 1);
      }
    };

    const handleFocus = () => {
      addLog("Window focused. Forcing Re-Sync...");
      setRefreshTrigger(prev => prev + 1);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocus);

    // AUTH LISTENER: Force Web3 Sync when Firebase User logs in/out
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      addLog(`Auth State Changed: ${user ? user.email : 'No User'}. Forcing Web3 Sync.`);
      setRefreshTrigger(prev => prev + 1);
    });

    // Also listen for provider events if available
    if (walletProvider && walletProvider.on) {
      const handleChainChanged = (newChain) => {
        addLog(`Provider Event: chainChanged to ${newChain}`);
        setRefreshTrigger(prev => prev + 1);
      };
      const handleAccountsChanged = (newAccounts) => {
        addLog(`Provider Event: accountsChanged`);
        setRefreshTrigger(prev => prev + 1);
      };

      walletProvider.on('chainChanged', handleChainChanged);
      walletProvider.on('accountsChanged', handleAccountsChanged);

      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("pageshow", handlePageShow);
        window.removeEventListener("focus", handleFocus);
        unsubscribeAuth(); // Clean up Auth listener
        walletProvider.removeListener('chainChanged', handleChainChanged);
        walletProvider.removeListener('accountsChanged', handleAccountsChanged);
      };
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocus);
      unsubscribeAuth();
    };
  }, [walletProvider]);


  useEffect(() => {
    const syncWeb3 = async () => {
      // Logic refactored to fetch ChainID from Provider directly (source of truth)

      const effectiveProvider = walletProvider;

      // We allow sync if we have an effective provider. 
      // If we rely solely on 'isConnected' from AppKit, we might miss the initial "injected" state.
      if (effectiveProvider) {
        try {
          const ethersProvider = new HybridBrowserProvider(effectiveProvider);

          // 1. Explicit Account Check
          // We must query accounts to see if we are TRULY connected (authorized)
          let accounts = [];
          try {
            accounts = await ethersProvider.listAccounts();
          } catch (accErr) {
            addLog(`Error listing accounts: ${accErr.message}`);
          }

          if (accounts.length === 0) {
            // STRICT MODE: If AppKit thinks we are connected but provider has no accounts,
            // it means the wallet is LOCKED or permission was revoked.
            if (isConnected) {
              addLog("Wallet appears LOCKED (No accounts returned). Forcing Re-Connect.");

              // Only disconnect if we are in a state that implies connection
              // This clears the "stale" state so the user can click "Connect" again and trigger the password prompt.
              setWallet(null);
              setProvider(null);
              setSigner(null);

              // We don't auto-disconnect via AppKit here to avoid loop if possible, 
              // but we MUST clear our internal state effectively.
              // Letting the UI show "Connect Wallet" is safer than showing "Connected with 0 balance".

              return;
            }
            return;
          }

          const currentAddress = accounts.length > 0 ? accounts[0].address : address;
          if (!currentAddress) {
            addLog("No address found. Aborting sync.");
            return;
          }

          addLog(`Syncing with Address: ${currentAddress}`);

          // 3. Get REAL Chain ID from Provider
          const network = await ethersProvider.getNetwork();
          const detectedChainId = Number(network.chainId);
          addLog(`Detected ChainID: ${detectedChainId}`);

          // 4. Auto-Switch Logic
          // STRICT MAINNET ONLY
          const ALLOWED_CHAINS = [56];

          if (!ALLOWED_CHAINS.includes(detectedChainId)) {
            addLog(`Wrong Chain (${detectedChainId}). Waiting for AppKit Auto-Switch...`);

            // Clear ALL state if on wrong chain to prevent stale contracts from sending on wrong session
            setWallet(null);
            setProvider(null);
            setSigner(null);
            setContract(null);
            setCrowdsaleContract(null);
            setBuybackContract(null);
            setBnbAtmContract(null);
            setBnbBuybackContract(null);
            setUsdtContract(null);

            // DELAY: Give AppKit 2 seconds to handle the switch automatically
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Re-Check Chain ID after delay
            const updatedNetwork = await ethersProvider.getNetwork();
            const updatedChainId = Number(updatedNetwork.chainId);

            if (!ALLOWED_CHAINS.includes(updatedChainId)) {
              addLog(`Still on Wrong Chain (${updatedChainId}). Requesting Manual Switch...`);
              try {
                const TARGET_CHAIN_HEX = '0x38';

                const switchPromise = effectiveProvider.request({
                  method: 'wallet_switchEthereumChain',
                  params: [{ chainId: TARGET_CHAIN_HEX }],
                });

                const timeoutPromise = new Promise((_, reject) =>
                  setTimeout(() => reject(new Error("Switch Request Timed Out")), 5000)
                );

                await Promise.race([switchPromise, timeoutPromise]);
                addLog("Switch Request Sent.");
              } catch (switchError) {
                if (switchError.code === -32002 || switchError.message.includes("pending")) {
                  addLog("Switch already pending (AppKit is handling it). Ignoring.");
                } else {
                  addLog(`Switch Error: ${switchError.message}`);
                }
              }
            } else {
              addLog(`Chain switched automatically to ${updatedChainId}. OK.`);
            }
          } else {
            addLog(`Chain is Valid (${detectedChainId}). Initializing Signer & Contracts...`);

            // 2. Initialize Signer & Provider (ONLY IF CHAIN IS VALID)
            const signerInstance = await ethersProvider.getSigner();
            setProvider(ethersProvider);
            setSigner(signerInstance);
            addLog("Provider & Signer Initialized.");

            // 5. Initialize Contracts
            try {
              // Helper to handle both { abi: [...] } and [...] formats
              const getAbi = (json) => json.abi || json;

              setContract(new ethers.Contract(XCOIN_ADDRESS, getAbi(XCoinSeriesA), signerInstance));
              setCrowdsaleContract(new ethers.Contract(CROWDSALE_ADDRESS, getAbi(XCoinCrowdsale), signerInstance));
              setBuybackContract(new ethers.Contract(BUYBACK_ADDRESS, getAbi(XCoinBuyback), signerInstance));
              setBnbAtmContract(new ethers.Contract(BNB_EXCHANGE_ADDRESS, getAbi(XCoinBNBExchange), signerInstance));
              setBnbBuybackContract(new ethers.Contract(BNB_EXCHANGE_ADDRESS, getAbi(XCoinBNBExchange), signerInstance));
              setUsdtContract(new ethers.Contract(USDT_ADDRESS, getAbi(MockUSDT), signerInstance));

              addLog("Contracts Initialized Successfully.");
            } catch (err) {
              addLog(`Contract Init Failed: ${err.message}`);
              console.error("Contract Initialization Error:", err);
            }

            // 6. Update UI State (Wallet Object)
            const chainIdHex = `0x${detectedChainId.toString(16)}`;
            let label = 'BNB Chain';

            setWallet({
              accounts: [{ address: currentAddress }],
              chains: [{ id: chainIdHex, label: label }],
              provider: walletProvider
            });

            // 7. Strict Binding & Firebase Sync (MOVED INSIDE VALID CHAIN BLOCK)
            if (auth.currentUser) {
              const userRef = doc(db, "usuarios", auth.currentUser.uid);
              try {
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                  const userData = userSnap.data();
                  const boundWallet = userData.walletAddress;
                  if (boundWallet && currentAddress) {
                    if (boundWallet.toLowerCase() === currentAddress.toLowerCase()) {
                      addLog("Wallet verified: Matches bound account.");
                    } else {
                      addLog(`CRITICAL: Wallet Mismatch! Account bound to ${boundWallet} but connected ${currentAddress}`);
                      alert(`⚠️ ERROR DE SEGURIDAD \n\nTu cuenta XCoin ya está vinculada a otra billetera:\n${boundWallet}\n\nPor favor cambia a esa cuenta en tu MetaMask para continuar.`);

                      // USE DEEP CLEAN HERE
                      await deepCleanSession("Security Mismatch (Bound != Connected)");
                      return;
                    }
                  } else {
                    if (bindingIntent) {
                      addLog("Wallet connected (No Profile). Binding Intent Detected -> Waiting for User Bind.");
                    } else {
                      addLog("SECURITY: Wallet connected but account has no wallet linked. No binding intent. Force Disconnecting.");

                      // USE DEEP CLEAN HERE
                      await deepCleanSession("Security Policy (No Bind + No Intent)");
                      return;
                    }
                  }
                }
              } catch (dbError) {
                addLog(`DB Error checking wallet strictness: ${dbError.message}`);
              }
            }
          }
        } catch (error) {
          addLog(`Critical Sync Error: ${error.message}`);
          setProvider(null);
          setSigner(null);
        }
      } else {
        // Disconnected
        if (isConnected && !address) addLog("Connected but waiting for Address...");
        else if (!isConnected) {
          if (wallet) addLog("Disconnected.");
          setWallet(null);
          setProvider(null);
          setSigner(null);
          setContract(null);
        }
      }
    };

    syncWeb3();
  }, [isConnected, walletProvider, address, refreshTrigger]); // Added refreshTrigger


  // --- 4. Exposed Functions ---

  const connectWallet = useCallback(async () => {
    addLog("User clicked Connect Wallet -> Showing Guide");
    setShowWalletGuide(true);
  }, []);

  const proceedToConnect = useCallback(async () => {
    addLog("User confirmed -> Opening WalletConnect");
    setShowWalletGuide(false);
    setBindingIntent(true);

    const isMobile = isMobileDevice();
    addLog(`Device detected: ${isMobile ? 'Mobile' : 'Desktop'}`);

    // AppKit handles WalletConnect internally for both desktop and mobile
    open();
  }, [open]);

  const forceRefresh = useCallback(() => {
    addLog("User clicked Manual Refresh");
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const disconnectWallet = useCallback(async () => {
    // Use helper
    await deepCleanSession("User Clicked Disconnect");
  }, [disconnect]);

  const unbindWallet = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      addLog("User clicked Unbind Wallet");
      const userRef = doc(db, "usuarios", auth.currentUser.uid);
      await setDoc(userRef, { walletAddress: null }, { merge: true });
      addLog("Wallet address removed from Firestore.");
      await disconnectWallet();
    } catch (e) {
      console.error("Unbind failed", e);
      addLog(`Unbind Error: ${e.message}`);
      throw e;
    }
  }, [disconnectWallet]);


  const manualBindWallet = async () => {
    if (!wallet || !wallet.accounts[0]) return;
    const address = wallet.accounts[0].address;

    addLog("Manual binding initiated...");
    const bindWalletFn = httpsCallable(functions, 'bindWallet');
    try {
      await bindWalletFn({ walletAddress: address });
      addLog("Manual bind success.");
      return true;
    } catch (err) {
      console.error(err);
      if (err.message.includes("already-exists") || err.code === 'already-exists') {
        alert("⚠️ Esta billetera ya está vinculada a otra cuenta.");
      } else {
        alert("Error al vincular: " + err.message);
      }
      throw err;
    }
  };

  return (
    <Web3Context.Provider value={{
      wallet,
      provider,
      signer,
      contract,
      crowdsaleContract,
      buybackContract,
      bnbAtmContract,
      bnbBuybackContract,
      usdtContract,
      connectWallet,
      disconnectWallet,
      unbindWallet,
      manualBindWallet, // Exposed
      forceRefresh, // Expose for UI button
      addTokenToWallet: async () => {
        if (!provider) return;
        try {
          const network = await provider.getNetwork();
          if (Number(network.chainId) !== 56) {
            alert("Por favor cámbiate a la red BNB Mainnet (Chain 56) para agregar el token oficial.");
            return;
          }
          if (!confirm(`¿Agregar Token XCO a MetaMask?\n\nContrato: ${XCOIN_ADDRESS}\nRed: BNB Mainnet`)) return;

          await provider.send("wallet_watchAsset", {
            type: "ERC20",
            options: {
              address: XCOIN_ADDRESS,
              symbol: "XCO",
              decimals: 18,
              image: "https://dashboard.xcoin.cl/logo.png",
            },
          });
          addLog(`Token XCO (${XCOIN_ADDRESS}) added to wallet`);
        } catch (error) {
          console.error("Error adding token:", error);
          addLog(`Error adding token: ${error.message}`);
        }
      },
      openNetworkModal: () => { addLog("Opening Network Modal"); open({ view: 'Networks' }); },
      isConnected, // Expose this for UI loading states
      web3Logs, // EXPOSE LOGS
      addLog, // EXPOSE LOG FUNCTION
      refreshTrigger // Expose Trigger for UI dependencies
    }}>
      <WalletGuideModal
        isOpen={showWalletGuide}
        onClose={() => setShowWalletGuide(false)}
        onProceed={proceedToConnect}
      />
      {children}
    </Web3Context.Provider>
  );
};

export const useWeb3 = () => {
  return useContext(Web3Context);
};
